import { isAxiosError } from 'axios';
import { showAccessDeniedToast } from './toast-utils';

/**
 * Returns true for transient Axios network errors (timeouts, network failures)
 * that should not be reported to Sentry because they are expected and recoverable.
 *
 * Axios error codes:
 * - ECONNABORTED: request timeout (axios `timeout` option exceeded)
 * - ERR_NETWORK: network-level failure (e.g. offline, DNS failure)
 *
 * Fallback: in some environments (browser XHR, certain Axios builds) the `code`
 * field may be undefined even for genuine network failures. Any AxiosError where
 * a request was sent but no response was received (`error.request && !error.response`)
 * is definitionally a network-level failure and should also be treated as transient.
 */
export const checkIsAxiosTransientError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  if (error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK')
    return true;
  // Fallback: request reached the network but no response came back.
  // This covers cases where Axios doesn't set a specific error code.
  if (error.request && !error.response) return true;
  return false;
};

export const checkIsAccessDeniedError = (error: unknown) => {
  if (!error) return false;

  if (isAxiosError(error) || (typeof error === 'object' && 'status' in error)) {
    return error.status === 403;
  }

  return false;
};

export const checkIsNotFoundError = (error: unknown): boolean => {
  if (!error) return false;

  if (isAxiosError(error) && error.response?.status === 404) {
    return true;
  }

  return false;
};

export const checkIsConflictError = (error: unknown) => {
  if (!error) return false;

  if (isAxiosError(error) && error.response?.status === 409) {
    return true;
  }

  return false;
};

/**
 * Returns true if the error is an insufficient credits error (code 8).
 * Checks both the error code and message for "insufficient credits".
 *
 * @param error - The error to check
 * @returns True if the error indicates insufficient credits
 */
export const checkIsInsufficientCreditsError = (error: unknown): boolean => {
  if (!error) return false;

  if (isAxiosError(error)) {
    const errorCode = error.response?.data?.code;
    const errorMessage = error.response?.data?.message;

    // Check for code 8 OR message contains "insufficient credits" (case-insensitive)
    return (
      errorCode === 8 ||
      (typeof errorMessage === 'string' &&
        errorMessage.toLowerCase().includes('insufficient credits'))
    );
  }

  return false;
};

/**
 * Structured error detail carried on a gRPC/REST error body as
 * `google.rpc.ErrorInfo` (see backend `tonic-types`). The transcoder emits it
 * under `response.data.details[]`; `reason` is a stable, machine-readable code
 * the UI can branch on, with optional `metadata` for structured context.
 */
export interface ApiErrorInfo {
  reason?: string;
  domain?: string;
  metadata?: Record<string, string>;
}

/**
 * Extract the `google.rpc.ErrorInfo` detail (if any) from an API error body.
 * Returns undefined when the error isn't an Axios error or carries no such
 * detail. Reads the loosely-typed `details[]` array on the `RpcStatus` body.
 */
export const getApiErrorInfo = (error: unknown): ApiErrorInfo | undefined => {
  if (!isAxiosError(error)) return undefined;
  const details = error.response?.data?.details;
  if (!Array.isArray(details)) return undefined;
  const info = details.find(
    (d): d is Record<string, unknown> =>
      typeof d?.['@type'] === 'string' &&
      (d['@type'] as string).endsWith('google.rpc.ErrorInfo'),
  );
  if (!info) return undefined;
  return {
    reason: typeof info.reason === 'string' ? info.reason : undefined,
    domain: typeof info.domain === 'string' ? info.domain : undefined,
    metadata:
      typeof info.metadata === 'object' && info.metadata !== null
        ? (info.metadata as Record<string, string>)
        : undefined,
  };
};

/** Convenience: the stable `reason` code from a `google.rpc.ErrorInfo` detail. */
export const getApiErrorReason = (error: unknown): string | undefined =>
  getApiErrorInfo(error)?.reason;

/**
 * The technical `message` from an API error body, if present. This is the
 * `google.rpc.Status.message` — precise, developer-oriented (may reference proto
 * field paths). Prefer `getApiLocalizedMessage` for display; use this for logs.
 */
export const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!isAxiosError(error)) return undefined;
  const message = error.response?.data?.message;
  return typeof message === 'string' ? message : undefined;
};

/**
 * The user-facing display copy from a `google.rpc.LocalizedMessage` detail, if
 * the backend attached one. This is the display-grade counterpart to the
 * technical `Status.message`, so it is preferred for anything shown to a user.
 */
export const getApiLocalizedMessage = (error: unknown): string | undefined => {
  if (!isAxiosError(error)) return undefined;
  const details = error.response?.data?.details;
  if (!Array.isArray(details)) return undefined;
  const localized = details.find(
    (d): d is Record<string, unknown> =>
      typeof d?.['@type'] === 'string' &&
      (d['@type'] as string).endsWith('google.rpc.LocalizedMessage'),
  );
  const message = localized?.message;
  return typeof message === 'string' && message.trim() !== ''
    ? message
    : undefined;
};

/**
 * Prefix used when the axios interceptor wraps a token-fetch failure into a
 * rejected request error.  Exported so that `axios-interceptor-provider.tsx`
 * constructs the message from the same constant — keeping both sides in sync.
 * If the format ever changes, updating this one string is sufficient.
 */
export const TOKEN_FETCH_ERROR_PREFIX =
  'Failed to get access token for API request';

/**
 * Returns true if the error originates from the token-manager failing to obtain
 * an access token (e.g. Auth0 session expiry / missing refresh token).
 *
 * These errors are already captured by `token-manager.ts` via `captureException`
 * before the rejected promise reaches React Query. Checking for them in
 * `meta.onError` handlers prevents duplicate Sentry events for the same failure.
 */
export const isTokenFetchError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  return error.message.startsWith(TOKEN_FETCH_ERROR_PREFIX);
};

/**
 * `@type` of the `google.rpc.ErrorInfo` detail our APIs attach to error
 * responses. The REST envelope carries it inside `details`:
 *
 * ```json
 * {
 *   "code": 412, "status": "FAILED_PRECONDITION", "message": "…",
 *   "details": [{
 *     "@type": "type.googleapis.com/google.rpc.ErrorInfo",
 *     "reason": "THREAD_DEPLOY_UP_TO_DATE", "domain": "rain", "metadata": {}
 *   }]
 * }
 * ```
 */
const ERROR_INFO_TYPE_URL = 'type.googleapis.com/google.rpc.ErrorInfo';

/**
 * The stable, machine-readable `reason` from an error's `google.rpc.ErrorInfo`
 * detail, or undefined when the error carries none — non-Axios errors, network
 * failures, and services that don't emit ErrorInfo yet all land here.
 */
const readErrorReason = (error: unknown): string | undefined => {
  if (!isAxiosError(error)) return undefined;

  const details: unknown = error.response?.data?.details;
  if (!Array.isArray(details)) return undefined;

  for (const detail of details) {
    if (!detail || typeof detail !== 'object') continue;
    const entry = detail as Record<string, unknown>;
    if (entry['@type'] !== ERROR_INFO_TYPE_URL) continue;
    const { reason } = entry;
    if (typeof reason === 'string' && reason) return reason;
  }

  return undefined;
};

/** What `errorHandler` hands to its fallback callback. */
export interface IExtractedError {
  /** The backend's human-readable message. User-facing copy — may be reworded. */
  message?: string;
  /**
   * Stable `google.rpc.ErrorInfo` reason code (e.g. `THREAD_DEPLOY_UP_TO_DATE`).
   * Append-only and never renamed once shipped, so branching on it is safe where
   * matching on `message` is not. Undefined when the error carries no ErrorInfo.
   */
  reason?: string;
}

/**
 * Routes an API error: 403s show the shared access-denied toast, everything else
 * falls through to `onError` with whatever could be extracted from the envelope.
 *
 * Prefer branching on `extracted.reason` over `extracted.message` when the UI
 * needs to single out one specific failure — the reason is a stable contract,
 * the message is copy the backend can reword at will.
 *
 * @param error - The error to handle
 * @param onError - Called for any error that isn't a 403
 */
export const errorHandler = (
  error: unknown,
  onError?: (extractedError?: IExtractedError) => void,
) => {
  if (checkIsAccessDeniedError(error)) {
    showAccessDeniedToast();
    return;
  }

  const message = isAxiosError(error)
    ? (error.response?.data?.message as string | undefined)
    : undefined;
  const reason = readErrorReason(error);

  // Callers destructure the argument, so only pass one when there's something in
  // it — an error with neither field still invokes `onError` with undefined.
  if (message || reason) {
    onError?.({ message, reason });
    return;
  }

  onError?.();
};
